import React from 'react'
import { Package, Minus, Plus } from 'lucide-react'
import { useWholesale, WholesaleKit } from '../contexts/WholesaleContext'
import { WHOLESALE } from '../config/wholesale'

const WholesaleKitSelector: React.FC = () => {
  const { selectedKit, setSelectedKit, quantity, setQuantity } = useWholesale()

  const handleSelect = () => {
    const kit: WholesaleKit = {
      size: WHOLESALE.kit.size,
      price: WHOLESALE.kit.price,
      label: WHOLESALE.kit.label,
    }
    setSelectedKit(kit)
  }

  const isSelected = selectedKit?.size === WHOLESALE.kit.size

  const handleDecrement = () => {
    setQuantity(Math.max(WHOLESALE.minKitsPerOrder, quantity - 1))
  }

  const handleIncrement = () => {
    setQuantity(Math.min(WHOLESALE.maxKitsPerOrder, quantity + 1))
  }

  return (
    <div className="mb-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold !text-white mb-3 font-heading">
          The 30-Pack Wholesale Kit
        </h2>
        <p className="text-lg !text-white/60 max-w-2xl mx-auto">
          One kit. Thirty tails. Zero prep on your line.
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        <button
          onClick={handleSelect}
          className={`relative w-full flex flex-col items-center justify-center p-6 md:p-10 rounded-2xl border-3 transition-all duration-300 ${
            isSelected
              ? 'border-[#962831] bg-gradient-to-br from-[#962831] to-[#6B2A2A] !text-white shadow-2xl'
              : 'border-white/20 hover:border-[#962831] bg-white/5 !text-white transform hover:scale-[1.02] hover:shadow-2xl'
          }`}
        >
          {/* Icon */}
          <div className={`mb-4 ${isSelected ? '!text-white' : '!text-[#DAA520]'}`}>
            <Package className="w-12 h-12 !text-inherit" />
          </div>

          {/* Kit Size */}
          <div className={`text-5xl md:text-6xl font-bold mb-2 ${isSelected ? '!text-white' : '!text-[#DAA520]'}`}>
            {WHOLESALE.kit.size}
          </div>

          {/* Label */}
          <div className={`text-xl md:text-2xl font-semibold mb-3 ${isSelected ? '!text-white' : '!text-white'}`}>
            {WHOLESALE.kit.label}
          </div>

          {/* Price */}
          <div className={`text-3xl md:text-4xl font-bold mb-3 ${isSelected ? '!text-white' : '!text-white'}`}>
            ${WHOLESALE.kit.price.toFixed(2)}
          </div>

          {/* Price per tail */}
          <div className={`text-base mb-4 ${isSelected ? '!text-white/90' : '!text-white/50'}`}>
            ${WHOLESALE.kit.pricePerTail.toFixed(2)} per tail
          </div>

          {/* Suggested retail margin note */}
          <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
            isSelected
              ? 'bg-white/20 !text-white'
              : 'bg-[#DAA520]/10 border border-[#DAA520]/30 !text-[#DAA520]'
          }`}>
            Suggested retail: ${WHOLESALE.kit.suggestedRetailLow}–${WHOLESALE.kit.suggestedRetailHigh} per tail
          </div>

          {/* Selected indicator */}
          {isSelected && (
            <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="#962831" strokeWidth="3" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>
      </div>

      {/* Quantity Stepper */}
      {isSelected && (
        <div className="max-w-2xl mx-auto mt-6">
          <div className="flex items-center justify-center gap-6 bg-white/5 border border-white/10 rounded-xl p-4">
            <span className="text-sm font-semibold !text-white/70">Quantity</span>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDecrement}
                disabled={quantity <= WHOLESALE.minKitsPerOrder}
                className="w-10 h-10 rounded-lg border border-white/20 flex items-center justify-center !text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-2xl font-bold !text-white min-w-[3rem] text-center font-heading">
                {quantity}
              </span>
              <button
                onClick={handleIncrement}
                disabled={quantity >= WHOLESALE.maxKitsPerOrder}
                className="w-10 h-10 rounded-lg border border-white/20 flex items-center justify-center !text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <span className="text-sm !text-white/50">
              {quantity} {quantity === 1 ? 'kit' : 'kits'} · {quantity * WHOLESALE.kit.size} tails total
            </span>
          </div>
          <div className="text-center mt-3">
            <span className="text-lg font-bold !text-[#DAA520]">
              ${(WHOLESALE.kit.price * quantity).toFixed(2)}
            </span>
            <span className="text-sm !text-white/50 ml-2">
              (${WHOLESALE.kit.price.toFixed(2)} per kit)
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default WholesaleKitSelector
